import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getGatewayConfig, addAgent, removeAgent } from '../api/gateway';
import { useWorkspaces } from '../hooks/useClaw9';

/**
 * Panel for managing gateway agents
 */
function AgentManagerPanel({ className = '' }) {
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [removeConfirmId, setRemoveConfirmId] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);

    // Form state
    const [newAgentName, setNewAgentName] = useState('');
    const [newAgentWorkspace, setNewAgentWorkspace] = useState('');

    const { workspaces } = useWorkspaces();

    const fetchAgents = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getGatewayConfig();
            setAgents(result.data?.agents || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    const handleAddAgent = async (e) => {
        e.preventDefault();
        if (!newAgentName.trim() || !newAgentWorkspace) {
            setError('Agent name and workspace are required.');
            return;
        }

        setIsAdding(true);
        setError(null);
        try {
            await addAgent({
                name: newAgentName.trim(),
                workspaceId: newAgentWorkspace
            });
            setNewAgentName('');
            setNewAgentWorkspace('');
            setShowAddForm(false);
            await fetchAgents();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsAdding(false);
        }
    };

    const handleRemoveAgent = async (agentId) => {
        setIsRemoving(true);
        try {
            await removeAgent(agentId);
            setRemoveConfirmId(null);
            await fetchAgents();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsRemoving(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
            case 'online':
                return 'bg-green-100 text-green-800';
            case 'inactive':
            case 'offline':
                return 'bg-gray-100 text-gray-800';
            case 'error':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                    <h4 className="text-sm font-medium text-gray-900">Agents</h4>
                    <p className="text-xs text-gray-500 mt-1">
                        {agents.length} agent{agents.length !== 1 ? 's' : ''} configured
                    </p>
                </div>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                    {showAddForm ? 'Cancel' : 'Add Agent'}
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="p-4 bg-red-50 border-b border-red-100">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Add Form */}
            {showAddForm && (
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <form onSubmit={handleAddAgent} className="space-y-3">
                        <div>
                            <label htmlFor="agent-name" className="block text-sm font-medium text-gray-700">
                                Agent Name
                            </label>
                            <input
                                id="agent-name"
                                type="text"
                                value={newAgentName}
                                onChange={(e) => setNewAgentName(e.target.value)}
                                placeholder="Enter agent name"
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={isAdding}
                            />
                        </div>
                        <div>
                            <label htmlFor="agent-workspace" className="block text-sm font-medium text-gray-700">
                                Workspace
                            </label>
                            <select
                                id="agent-workspace"
                                value={newAgentWorkspace}
                                onChange={(e) => setNewAgentWorkspace(e.target.value)}
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={isAdding}
                            >
                                <option value="">Select a workspace</option>
                                {workspaces.map(ws => (
                                    <option key={ws.id} value={ws.id}>
                                        {ws.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="submit"
                            disabled={isAdding}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {isAdding ? 'Adding...' : 'Add Agent'}
                        </button>
                    </form>
                </div>
            )}

            {/* Agent List */}
            <div className="divide-y divide-gray-200">
                {isLoading && agents.length === 0 ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto" />
                    </div>
                ) : agents.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-sm">
                        No agents configured
                    </div>
                ) : (
                    agents.map(agent => (
                        <div key={agent.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                    <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                                    <p className="text-xs text-gray-500">{agent.workspaceId}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agent.status)}`}>
                                    {agent.status || 'Unknown'}
                                </span>
                                {removeConfirmId === agent.id ? (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleRemoveAgent(agent.id)}
                                            disabled={isRemoving}
                                            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                                        >
                                            {isRemoving ? 'Removing...' : 'Confirm'}
                                        </button>
                                        <button
                                            onClick={() => setRemoveConfirmId(null)}
                                            className="text-sm text-gray-600 hover:text-gray-800"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setRemoveConfirmId(agent.id)}
                                        className="text-sm text-red-600 hover:text-red-800"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

AgentManagerPanel.propTypes = {
    className: PropTypes.string
};

export default AgentManagerPanel;
